import React, {useState, useEffect}  from "react";
import { useParams } from "react-router-dom";

// Section for Utilities and Context
import "./profile.css";
import API from "../utils/API";

// Section for Components
import ProfileCard from "../components/ProfileCard";
import BioBox from "../components/BioBox";
import JobList from "../components/JobList";
import ColleagueList from "../components/ColleagueList";
// import StaffedBills from "../components/StaffedBills";
import ProfileIssues from "../components/ProfileIssues";
import OrgActivityFeed from "../components/OrgActivityFeed";
import AddJobModal from "../components/AddJobModal";
import EditJobModal from "../components/EditJobModal";
import EditProfileModal from "../components/EditProfileModal";
import PageTitle from "../components/PageTitle";
import DistrictInfo from "../components/DistrictInfo";

const TAB_STORAGE_TTL_MS = 60 * 60 * 1000;

function readStoredTab(profileId) {
    if (!profileId) return undefined;
    try {
        const raw = localStorage.getItem(`profileTab:${profileId}`);
        if (!raw) return undefined;
        const { tab, savedAt } = JSON.parse(raw);
        if (!tab || Date.now() - savedAt > TAB_STORAGE_TTL_MS) return undefined;
        return tab;
    } catch {
        return undefined;
    }
}

export default function Profile() {
    const [singleProfile, setSingleProfile] = useState();
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState(() => readStoredTab(id) || "Overview");
    const [postsTypeFilter, setPostsTypeFilter] = useState(undefined);
    const [postsTypesList, setPostsTypesList] = useState([]);
    const [jobBeingEdited, setJobBeingEdited] = useState({});

    useEffect(() => {
        try {
            localStorage.setItem(`profileTab:${id}`, JSON.stringify({ tab: activeTab, savedAt: Date.now() }));
        } catch {}
    }, [id, activeTab]);

    // A restored "District Info" tab means nothing for a profile that isn't a district's elected official.
    useEffect(() => {
        const job = singleProfile?.data?.JobExperiences?.[0];
        const hasDistrict = job?.current_job && job?.office_type === "Elected" && job?.Division?.district;
        if (singleProfile && activeTab === "District Info" && !hasDistrict) setActiveTab("Overview");
    }, [singleProfile, activeTab]);

    useEffect(() => {
        // GET SINGLE PROFILE INFO FROM DATABASE FOR PROFILE PAGE
        const getProfile = async () => {        
            try {
                const data = await API.getSingleProfile(id); 
                // console.log("Fetched Profile Data:", data.data);
                setSingleProfile(data);
            } catch (error) {
                console.log("Error fetching single profile:", error);
            }
        };

        getProfile();
    }, [id]);  

    // GET SINGLE PROFILE INFO FROM DATABASE FOR PROFILE PAGE
    const fetchSingleProfile = async () => {        
        try {
            const data = await API.getSingleProfile(id); 
            // console.log("Fetched Profile Data:", data.data);
            setSingleProfile(data);
        } catch (error) {
            console.log("Error fetching single profile:", error);
        }
    };
        
    
    // HANDLE NULL STATE PRIOR TO RENDERING CORRECTLY
    if (!singleProfile) {
        return (
            <div className="d-flex position-absolute top-50 start-50 translate-middle ">
                <div className="spinner-border loading-spinner" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>);
    }

    const topJob = singleProfile.data.JobExperiences[0];
    const electedDistrict = topJob?.current_job && topJob?.office_type === "Elected" ? topJob?.Division?.district : null;

    const fullName = `${singleProfile.data.first_name || ''} ${singleProfile.data.last_name || ''}`.trim();

    return (
        <div className="profile-page">
            {fullName && <PageTitle title={`${fullName} | 'elloGovna`} />}
            <div className="profile-page-columns">
                <div className="profile-sidebar">
                    <div className="profile-card">
                        <ProfileCard
                            index={singleProfile.data.profile_id}
                            user_id={singleProfile.data.user_id}
                            firstName={singleProfile.data.first_name}
                            lastName={singleProfile.data.last_name}
                            title={singleProfile.data.JobExperiences[0]?.title || "Not Available"}
                            district={electedDistrict}
                            organization={singleProfile.data.JobExperiences[0]?.Division?.division_name || singleProfile.data.JobExperiences[0]?.Organization?.organization_name || "Not Available"}
                            organization_id={singleProfile.data.JobExperiences[0]?.Organization?.organization_id || null}
                            division_id={singleProfile.data.JobExperiences[0]?.Division?.division_id || null}
                            profile_picture={singleProfile.data.profile_picture}
                            email={singleProfile.data.work_email}
                            personal_email={singleProfile.data.personal_email}
                            phone={singleProfile.data.other_phone}
                            mobile_phone={singleProfile.data.mobile_phone}
                            address={singleProfile.data.JobExperiences[0]?.street_address || singleProfile.data.JobExperiences[0]?.Organization?.street_address || null}
                            suite_number={singleProfile.data.JobExperiences[0]?.street_address ? singleProfile.data.JobExperiences[0]?.suite_number : (singleProfile.data.JobExperiences[0]?.Division?.suite_number || singleProfile.data.JobExperiences[0]?.Organization?.suite_number || null)}
                            city={singleProfile.data.JobExperiences[0]?.city || singleProfile.data.JobExperiences[0]?.Organization?.city || null}
                            stateCode={singleProfile.data.JobExperiences[0]?.state || singleProfile.data.JobExperiences[0]?.Organization?.state || null}
                            zipCode={singleProfile.data.JobExperiences[0]?.zip_code || singleProfile.data.JobExperiences[0]?.Organization?.zip_code || null}
                            website={singleProfile.data.JobExperiences[0]?.Division?.website || singleProfile.data.JobExperiences[0]?.Organization?.website || null} 
                            linkedin_url={singleProfile.data.linkedin_url || null}
                            facebook_url={singleProfile.data.facebook_url || null}
                            youtube_url={singleProfile.data.youtube_url || null}
                            threads_url={singleProfile.data.threads_url || null}
                            twitter_url={singleProfile.data.twitter_url || null}
                            instagram_url={singleProfile.data.instagram_url || null}
                            tiktok_url={singleProfile.data.tiktok_url || null}
                            bluesky_url={singleProfile.data.bluesky_url || null}
                            pronouns={singleProfile.data.pronouns || null}
                            lobby_filer_id={singleProfile.data.lobby_filer_id}
                            />
                    </div>
                    {/* <div className="profile-ad-box">
                        <AdBox />
                    </div> */}
                </div>
                <div className="profile-main">
                    <div className="profile-tabs-sticky">
                        <h6 className="profile-tabs">
                            <span className={`me-5 profile-tab ${activeTab === "Overview" ? "selected" : ""}`} onClick={() => setActiveTab("Overview")}>Overview</span>
                            <span className={`me-5 profile-tab ${activeTab === "History" ? "selected" : ""}`} onClick={() => setActiveTab("History")}>History</span>
                            <span className={`me-5 profile-tab ${activeTab === "Network" ? "selected" : ""}`} onClick={() => setActiveTab("Network")}>Network</span>
                            <span className={`me-5 profile-tab ${activeTab === "Posts" ? "selected" : ""}`} onClick={() => setActiveTab("Posts")}>Posts</span>
                            <span className={`me-5 profile-tab ${activeTab === "Stats" ? "selected" : ""}`} onClick={() => setActiveTab("Stats")}>Stats</span>
                            {electedDistrict && (
                                <span className={`me-5 profile-tab ${activeTab === "District Info" ? "selected" : ""}`} onClick={() => setActiveTab("District Info")}>District Info</span>
                            )}
                        </h6>
                        <hr className="profile-tabs-divider" />
                        {activeTab === "Posts" && postsTypesList.length > 0 && (
                            <div className="profile-posts-filter-bar">
                                <select
                                    className="form-select people-type-filter-select"
                                    value={postsTypeFilter !== undefined ? postsTypeFilter : ""}
                                    onChange={(e) => setPostsTypeFilter(e.target.value)}
                                >
                                    <option value="" disabled>Post Type</option>
                                    {postsTypesList.map(type => (
                                        <option key={type.posttype_id} value={type.posttype_id}>{type.post_type}</option>
                                    ))}
                                </select>
                                <button
                                    className="clear-people-filter-button"
                                    onClick={() => setPostsTypeFilter(undefined)}
                                >Reset</button>
                            </div>
                        )}
                    </div>

                    <div className="profile-tab-content">
                    {activeTab === "Overview" && (
                        <div className="profile-overview">
                            <div className="profile-overview-card">
                                <BioBox
                                    key={"bio" + singleProfile.data.last_name + singleProfile.data.profile_id}
                                    profile_id={singleProfile.data.profile_id}
                                    firstName={singleProfile.data.first_name}
                                    bio={singleProfile.data.user_bio}
                                    user_id={singleProfile.data.user_id}
                                    reRenderProfile={fetchSingleProfile}
                                />
                            </div>
                            <div className="profile-overview-card">
                                <ProfileIssues
                                    issues={singleProfile.data.UserIssues || "None"}
                                    user_id={singleProfile.data.user_id}
                                    profile_id={singleProfile.data.profile_id}
                                    reRenderProfile={fetchSingleProfile}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === "History" && (
                        <div className="profile-history">
                            <div className="profile-job-list">
                                <JobList
                                    job_id={singleProfile.data.JobExperiences.job_id}
                                    last_name={singleProfile.data.last_name}
                                    profile_id={singleProfile.data.profile_id}
                                    experience={singleProfile.data.JobExperiences}
                                    user_id={singleProfile.data.user_id}
                                    current={singleProfile.data.current_job}
                                    onEditPosition={setJobBeingEdited}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === "Network" && (
                        <div className="profile-colleague-list">
                            <ColleagueList
                                viewAll
                                profile_id={singleProfile.data.profile_id}
                                colleague_team={
                                    singleProfile.data.JobExperiences[0]?.Division?.division_name ||
                                    singleProfile.data.JobExperiences[0]?.Organization?.organization_name ||
                                    "Not Available"}
                                organization_id={
                                    singleProfile.data.JobExperiences[0]?.Organization?.organization_id ||
                                    null
                                }
                                division_id={
                                    singleProfile.data.JobExperiences[0]?.Division?.division_id ||
                                    null
                                }
                                />
                        </div>
                    )}

                    {activeTab === "Posts" && (
                        <div className="profile-posts">
                            <OrgActivityFeed
                                post_author={singleProfile.data.profile_id}
                                showSearch={false}
                                externalFilterBar
                                postTypeFilter={postsTypeFilter}
                                onPostTypeFilterChange={setPostsTypeFilter}
                                onPostTypesLoaded={setPostsTypesList}
                            />
                        </div>
                    )}

                    {activeTab === "District Info" && electedDistrict && (
                        <DistrictInfo district={electedDistrict} />
                    )}

                    {activeTab === "Stats" && (
                        <div className="profile-stats-placeholder">
                            <i className="bi bi-bar-chart-line" />
                            <p className="mb-0">Stats are coming soon.</p>
                        </div>
                    )}
                    </div>
                </div>
            </div>

            <EditProfileModal
                data={singleProfile.data}
                profile_picture={singleProfile.data.profile_picture}
                reRenderProfile={fetchSingleProfile}
            />
            <AddJobModal
                profile_id={singleProfile.data.profile_id}
                reRenderProfile={fetchSingleProfile}
            />
            <EditJobModal
                data={jobBeingEdited}
                reRenderProfile={fetchSingleProfile}
                expLength={singleProfile.data.JobExperiences.length}
            />
        </div>
    )
}